import qs from 'qs'
import { Method, TEN_MINUTE_CACHE, RequestOptions } from './types/api'
import { HTTPStatusCode } from './types/statusCodes'

/**
 * Generates a valid API URL given an endpoint and request parameters.
 * @param baseUrl
 * @param endpoint
 * @param params
 * @param paramsSeralizationOptions
 */
export function getApiUrl<P extends object>(
  baseUrl: string,
  endpoint: string,
  params?: P,
  paramsSeralizationOptions?: qs.IStringifyOptions
) {
  let url = `${baseUrl}${endpoint}`
  if (params && Object.keys(params).length) {
    url += `?${qs.stringify(params, paramsSeralizationOptions)}`
  }

  return url
}

export class ApiError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

/**
 * Attempt to recursively parse json from api response to find the error message
 * @param response
 */
export function getMessageFromJsonRecursive(response: unknown): string {
  let message: unknown =
    // @ts-ignore
    response?.error ??
    // @ts-ignore
    response?.message ??
    // @ts-ignore
    response?.msg ??
    // @ts-ignore
    response?.detail

  if (typeof message === 'string') {
    return message
  } else if (Array.isArray(message)) {
    return (message = message
      .map((innerMessage: unknown) => getMessageFromJsonRecursive(innerMessage))
      .join(', '))
  } else if (message) {
    return JSON.stringify(message)
  }
  return JSON.stringify(response)
}

/**
 * Performs a request to the API.
 * @param method
 * @param endpoint
 * @param options
 * @param baseUrl
 * @throws {ApiError} any errors during the request
 */
export async function apiRequest(
  method: Method,
  endpoint: string,
  options: RequestOptions,
  baseUrl: string
) {
  // NOTE: this does not pass an Authorization header,
  // Do not use from ThymeBox, use apps/thymebox/src/libs/api.ts
  const headers = new Headers()

  if (options.body && Object.keys(options.body).length) {
    headers.append('Content-Type', 'application/json')
  }

  if (options.headers) {
    for (const [k, v] of Object.entries(options.headers)) {
      headers.append(k, v)
    }
  }

  if (method === 'GET' && !headers.has('Cache-Control') && !options.noCache) {
    headers.append('Cache-Control', TEN_MINUTE_CACHE)
  }

  let formData = undefined
  if (options.file) {
    formData = new FormData()
    formData.append('file', options.file)
  }

  const response = await fetch(
    getApiUrl(
      baseUrl,
      endpoint,
      options.params ?? {},
      options.paramSerializationOptions
    ),
    {
      method,
      headers,
      body: formData ? formData : JSON.stringify(options.body),
    }
  )

  if (response.status !== HTTPStatusCode.STATUS_204_NO_CONTENT.valueOf()) {
    /* eslint-disable @typescript-eslint/no-unsafe-assignment */
    /* eslint-disable @typescript-eslint/no-unsafe-argument */
    /* eslint-disable @typescript-eslint/no-unsafe-member-access */
    const res = await response.json()

    if (response.status >= HTTPStatusCode.ERROR_400_BAD_REQUEST.valueOf()) {
      throw new ApiError(getMessageFromJsonRecursive(res), response.status)
    }

    return res
  }

  return null
}
